// src/utils/roleTaxonomy.js
// --------------------------------------------------------------------------
// Fuente ÚNICA de verdad de la taxonomía de roles, categorizada según el flujo
// de trabajo (ver PROCESS-FLOW-CARTONBAR.md). La usan:
//   • las chips de roles en UsersView / RolesView (color por categoría),
//   • el panel de "Consolidación de roles" (audit) en RolesView,
//   • (a futuro) el script de migración messy → canónico.
//
// Contexto: los datos actuales tienen ~19 strings de rol redundantes (duplicados
// por mayúsculas, sinónimos, typos y combos). Aquí se define el set canónico
// (~12 roles en 6 categorías) y un mapa de sinónimos para clasificar lo existente.
// --------------------------------------------------------------------------

// Normaliza para comparar: sin acentos, minúsculas, espacios colapsados.
export const normalizeRole = (s) =>
    (s || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');

// Categorías ordenadas (alineadas a las lanes del proceso). `chip`/`dot` son
// clases Tailwind para colorear.
export const CATEGORIES = [
    { id: 'direccion', label: 'Dirección & Sistema', chip: 'bg-rose-100 text-rose-800', dot: 'bg-rose-500' },
    { id: 'ventas', label: 'Ventas (CRM + Sales)', chip: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
    { id: 'diseno', label: 'Pre-Sales (Diseño)', chip: 'bg-violet-100 text-violet-800', dot: 'bg-violet-500' },
    { id: 'produccion', label: 'Producción', chip: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
    { id: 'externos', label: 'Externos', chip: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
    { id: 'facturacion', label: 'Facturación', chip: 'bg-teal-100 text-teal-800', dot: 'bg-teal-500' },
    { id: 'sin-clasificar', label: 'Sin clasificar', chip: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
];

export const CATEGORY_BY_ID = CATEGORIES.reduce((acc, c) => ({ ...acc, [c.id]: c }), {});

// Set canónico de roles → categoría.
export const CANONICAL_ROLES = [
    { name: 'Admin', category: 'direccion' },
    { name: 'Project Manager', category: 'direccion' },
    { name: 'Ventas', category: 'ventas' },
    { name: 'Diseño', category: 'diseno' },
    { name: 'Prepress', category: 'diseno' },
    { name: 'Jefe de Producción', category: 'produccion' },
    { name: 'Operador', category: 'produccion' },
    { name: 'Finishing', category: 'produccion' },
    { name: 'Instalador', category: 'produccion' },
    { name: 'Estación', category: 'produccion' },
    { name: 'Partner', category: 'externos' },
    { name: 'Diseñador Freelance', category: 'externos' },
    { name: 'Cliente/Asociado', category: 'externos' },
    { name: 'Facturación', category: 'facturacion' },
];

const CATEGORY_OF_CANONICAL = CANONICAL_ROLES.reduce((acc, r) => ({ ...acc, [r.name]: r.category }), {});
const CANONICAL_BY_NORM = CANONICAL_ROLES.reduce((acc, r) => ({ ...acc, [normalizeRole(r.name)]: r.name }), {});

// Mapa de sinónimos (string normalizado → rol canónico, o array para combos).
const SYNONYMS = {
    'admin': 'Admin',
    'administrador': 'Admin',
    'project manager': 'Project Manager',
    'pm': 'Project Manager',
    'vendedor': 'Ventas',
    'ventas': 'Ventas',
    'diseno': 'Diseño',
    'disenador': 'Diseño',
    'prepress': 'Prepress',
    'produccion': 'Jefe de Producción',
    'jefe de produccion': 'Jefe de Producción',
    'operador': 'Operador',
    'operador de maquina': 'Operador',
    'finishing': 'Finishing',
    'acabados': 'Finishing',
    'instalacion': 'Instalador',
    'instalador': 'Instalador',
    'productor/instalador': ['Operador', 'Instalador'],
    'productor instalador': ['Operador', 'Instalador'],
    'partner': 'Partner',
    'partnert': 'Partner',
    'disenador partner': 'Diseñador Freelance',
    'diseno partner': 'Diseñador Freelance',
    'disenador partener': 'Diseñador Freelance',
    'disenador freelance': 'Diseñador Freelance',
    'freelance': 'Diseñador Freelance',
    'cliente/asociado': 'Cliente/Asociado',
    'cliente asociado': 'Cliente/Asociado',
    'cliente': 'Cliente/Asociado',
    'asociado': 'Cliente/Asociado',
    'facturacion': 'Facturación',
};

// Strings ambiguos: resuelven a un canónico pero conviene revisar a mano.
const AMBIGUOUS = {
    'produccion': '¿Jefe de Producción u Operador? Revisar.',
};

// Clasifica un string de rol (canónico o messy).
// → { input, canonical, split, category, status, note }
//   status: 'canonical' | 'rename' | 'combo' | 'ambiguous' | 'unknown'
export const classifyRole = (raw) => {
    const input = (raw || '').trim();
    const norm = normalizeRole(input);

    // Combo → varios canónicos.
    const syn = SYNONYMS[norm];
    if (Array.isArray(syn)) {
        return { input, split: syn, category: CATEGORY_OF_CANONICAL[syn[0]] || 'produccion', status: 'combo', note: `Combo → ${syn.join(' + ')}` };
    }
    if (syn) {
        const category = CATEGORY_OF_CANONICAL[syn] || 'sin-clasificar';
        if (AMBIGUOUS[norm]) return { input, canonical: syn, category, status: 'ambiguous', note: AMBIGUOUS[norm] };
        const isExact = CANONICAL_BY_NORM[norm] === input; // mismo nombre exacto (incl. mayúsculas/acentos)
        return { input, canonical: syn, category, status: isExact ? 'canonical' : 'rename', note: isExact ? null : `→ ${syn}` };
    }
    if (CANONICAL_BY_NORM[norm]) {
        const canonical = CANONICAL_BY_NORM[norm];
        const isExact = canonical === input;
        return { input, canonical, category: CATEGORY_OF_CANONICAL[canonical], status: isExact ? 'canonical' : 'rename', note: isExact ? null : `→ ${canonical}` };
    }
    return { input, canonical: null, category: 'sin-clasificar', status: 'unknown', note: 'Sin clasificar' };
};

// Clases Tailwind de chip para un string de rol (color por categoría).
export const chipClassForRole = (raw) => {
    const { category } = classifyRole(raw);
    return (CATEGORY_BY_ID[category] || CATEGORY_BY_ID['sin-clasificar']).chip;
};

// Tooltip útil: muestra la categoría y la sugerencia canónica si difiere.
export const roleTooltip = (raw) => {
    const r = classifyRole(raw);
    const cat = (CATEGORY_BY_ID[r.category] || {}).label || 'Sin clasificar';
    if (r.status === 'combo') return `${cat} · ${r.note}`;
    if (r.status === 'rename') return `${cat} · sugerido: ${r.canonical}`;
    if (r.status === 'ambiguous') return `${cat} · ${r.note}`;
    if (r.status === 'unknown') return 'Sin clasificar — no coincide con ningún rol canónico';
    return cat;
};
